import { ListingCarousel } from "@/components/index/fragments/ListingCarousel";
import { RankedEstateIntroSlide } from "@/components/index/fragments/RankedEstateIntroSlide";
import {
  RankedEstateFavoriteActions,
  RankedEstateFavoriteSlide,
} from "@/components/index/fragments/RankedEstateFavoriteSlide";
import { RankedEstateMembershipSlide } from "@/components/index/fragments/RankedEstateMembershipSlide";
import {
  RankedEstateHideAction,
  RankedEstateSlide,
} from "@/components/index/fragments/RankedEstateSlide";
import { Button } from "@/components/ui/Button";
import { PawLoader } from "@/components/ui/PawLoader";
import { useAdminContext } from "@/context/AdminContext";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import type { PublicWatchDogOffer, PublicWatchDogOfferGroup } from "@/types";
import { getEstateValueLabel } from "@/utils/estateValueLabels";
import { editResult, fetchPublicMostExpensiveWeeklyOffers } from "@/utils";
import {
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";

const SELECTED_RESULT_COOKIE = "softiq_admin_selected_expensive_result";
const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365;

function getSavedResultSelection() {
  if (typeof document === "undefined") return null;

  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${SELECTED_RESULT_COOKIE}=`));

  return cookie
    ? decodeURIComponent(cookie.split("=").slice(1).join("="))
    : null;
}

function saveResultSelection(selectionKey: string) {
  document.cookie = `${SELECTED_RESULT_COOKIE}=${encodeURIComponent(selectionKey)}; path=/; max-age=${ONE_YEAR_IN_SECONDS}; SameSite=Lax`;
}

function getCity(offer: PublicWatchDogOffer, fallback: string) {
  const location = offer.address || fallback;
  return location.replace(/\d+/g, "").trim() || fallback;
}

type AdminWatchDogPricesProps = {
  offerType?: "sale" | "rent" | "all";
  questionStartIndex?: number;
};

type LoadedOfferGroup = PublicWatchDogOfferGroup & {
  resultType: "sale" | "rent";
  selectionKey: string;
};

export default function AdminWatchDogPrices({
  offerType = "sale",
  questionStartIndex = 0,
}: AdminWatchDogPricesProps) {
  const { language } = useLanguage();
  const { isAdmin } = useAdminContext();
  const { userInfoGlobal } = useAppContext();
  const [showHideActions, setShowHideActions] = useState(false);
  const [groups, setGroups] = useState<LoadedOfferGroup[]>([]);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const copy = {
    cs: {
      label: "Nejdražší domy na prodej za posledních 7 dní",
      watchdog: "Hlídací pes",
      previous: "Předchozí",
      next: "Další",
      loadingLabel: "Načítání inzerátů",
      error: "Nejdražší inzeráty se nepodařilo načíst.",
      rentError: "Nejdražší pronájmy se nepodařilo načíst.",
      empty:
        "Tento hlídací pes nemá za posledních 7 dní žádné inzeráty s cenou.",
      rentEmpty:
        "Tento hlídací pes nemá za posledních 7 dní žádné pronájmy s cenou.",
      noLocation: "Česko",
      showActions: "Zobrazit administrační tlačítka",
      hideActions: "Skrýt administrační tlačítka",
      selectWatchdog: "Vyberte hlídacího psa",
      sale: "Prodej",
      rent: "Pronájem",
      refresh: "Znovu načíst",
    },
    en: {
      label: "Most expensive listings from the last 7 days",
      rentLabel: "Most expensive rentals from the last 7 days",
      watchdog: "Watchdog",
      previous: "Previous",
      next: "Next",
      loadingLabel: "Loading listings",
      error: "The most expensive listings could not be loaded.",
      rentError: "The most expensive rentals could not be loaded.",
      empty: "This watchdog has no priced listings from the last 7 days.",
      rentEmpty: "This watchdog has no priced rentals from the last 7 days.",
      noLocation: "Czechia",
      showActions: "Show admin buttons",
      hideActions: "Hide admin buttons",
      selectWatchdog: "Select a watchdog",
      sale: "Sale",
      rent: "Rent",
      refresh: "Refetch",
    },
  }[language];
  const isRent = offerType === "rent";
  const canLoadOffers = isAdmin;

  useEffect(() => {
    if (!canLoadOffers) return;

    let isActive = true;

    const loadOffers = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const response = await fetchPublicMostExpensiveWeeklyOffers();
        if (!isActive) return;

        if (response.status === 200 && Array.isArray(response.data.data)) {
          const nextGroups = (
            response.data.data as PublicWatchDogOfferGroup[]
          ).map((group) => ({
            ...group,
            resultType: "sale" as const,
            selectionKey: `sale-${group.watchdogId}`,
          }));
          setGroups(nextGroups);
          setSelectedGroupKey((currentKey) => {
            const savedKey = getSavedResultSelection();
            const preferredKey = currentKey ?? savedKey;

            return preferredKey !== null &&
              nextGroups.some((group) => group.selectionKey === preferredKey)
              ? preferredKey
              : (nextGroups[0]?.selectionKey ?? null);
          });
        } else {
          setHasError(true);
        }
      } catch {
        if (isActive) setHasError(true);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    void loadOffers();
    return () => {
      isActive = false;
    };
  }, [canLoadOffers, refreshKey]);

  if (!isAdmin) return null;

  const hideOffer = async (groupKey: string, offerId: number) => {
    if (!isAdmin || !userInfoGlobal?.token) return;

    const response = await editResult(
      {
        email: userInfoGlobal.email,
        token: userInfoGlobal.token,
        data: {
          watchDogEstateId: offerId,
          isFavorite: false,
          doHide: true,
        },
      },
      userInfoGlobal.token,
    );

    if (response.status !== 200) return;

    setGroups((currentGroups) =>
      currentGroups.map((group) =>
        group.selectionKey === groupKey
          ? {
              ...group,
              offers: group.offers.filter((offer) => offer.id !== offerId),
              favoriteOffers: (group.favoriteOffers ?? []).filter(
                (offer) => offer.id !== offerId,
              ),
            }
          : group,
      ),
    );
  };

  const unfavoriteOffer = async (groupKey: string, offerId: number) => {
    if (!isAdmin || !userInfoGlobal?.token) return;

    const response = await editResult(
      {
        email: userInfoGlobal.email,
        token: userInfoGlobal.token,
        data: {
          watchDogEstateId: offerId,
          isFavorite: false,
          doHide: false,
        },
      },
      userInfoGlobal.token,
    );

    if (response.status !== 200) return;

    setGroups((currentGroups) =>
      currentGroups.map((group) =>
        group.selectionKey === groupKey
          ? {
              ...group,
              favoriteOffers: (group.favoriteOffers ?? []).filter(
                (offer) => offer.id !== offerId,
              ),
            }
          : group,
      ),
    );
  };

  const visibleGroups =
    isAdmin && selectedGroupKey !== null
      ? groups.filter((group) => group.selectionKey === selectedGroupKey)
      : groups;
  const selectedIsRent = visibleGroups[0]?.resultType === "rent";

  return (
    <section className="admin-watchdog-snap relative z-10 flex w-full flex-col px-4 pb-8 pt-10 sm:px-8 lg:h-[100svh]">
      <div className="watchdog-text-root !mb-6 flex flex-wrap items-center justify-between gap-3">
        <span className="section-label">
          {selectedIsRent ? copy.rentLabel : copy.label}
        </span>
        {isAdmin && (
          <div className="flex w-full flex-nowrap items-center gap-2 sm:w-auto">
            {groups.length > 1 && (
              <label
                className="sr-only"
                htmlFor={`watchdog-select-${offerType}`}
              >
                {copy.selectWatchdog}
              </label>
            )}
            {groups.length > 1 && (
              <select
                id={`watchdog-select-${offerType}`}
                value={selectedGroupKey ?? ""}
                onChange={(event) => {
                  const nextSelection = event.target.value;
                  setSelectedGroupKey(nextSelection);
                  saveResultSelection(nextSelection);
                }}
                className="h-8 min-w-0 flex-1 cursor-pointer rounded-md border border-input bg-background px-2 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-accent sm:min-w-44 sm:px-3"
                aria-label={copy.selectWatchdog}
              >
                {groups.map((group) => (
                  <option key={group.selectionKey} value={group.selectionKey}>
                    {group.resultType === "rent" ? copy.rent : copy.sale} ·{" "}
                    {getEstateValueLabel(group.estateType, language)}
                  </option>
                ))}
              </select>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isLoading}
              onClick={() => setRefreshKey((current) => current + 1)}
              className="w-8 shrink-0 px-0 sm:w-auto sm:px-3"
              aria-label={copy.refresh}
              title={copy.refresh}
            >
              <ArrowPathIcon
                className={`size-4 ${isLoading ? "animate-spin" : ""}`}
              />
              <span className="sr-only sm:not-sr-only">{copy.refresh}</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={showHideActions}
              onClick={() => setShowHideActions((current) => !current)}
              className="w-8 shrink-0 px-0 sm:w-auto sm:px-3"
              aria-label={showHideActions ? copy.hideActions : copy.showActions}
              title={showHideActions ? copy.hideActions : copy.showActions}
            >
              {showHideActions ? (
                <EyeSlashIcon className="size-4" />
              ) : (
                <EyeIcon className="size-4" />
              )}
              <span className="sr-only sm:not-sr-only">
                {showHideActions ? copy.hideActions : copy.showActions}
              </span>
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <PawLoader label={copy.loadingLabel} />
      ) : hasError ? (
        <p
          className="py-16 text-center text-lg text-softiq-copy-muted dark:text-softiq-copy-inverse-muted"
          role="alert"
        >
          {isRent ? copy.rentError : copy.error}
        </p>
      ) : (
        <div
          className={`grid grid-cols-1 gap-x-8 lg:min-h-0 lg:flex-1 ${isAdmin ? "" : "lg:grid-cols-2"}`}
        >
          {visibleGroups.map((group) => {
            const groupIndex = groups.findIndex(
              (candidate) => candidate.selectionKey === group.selectionKey,
            );
            const offers = group.offers.slice(0, 3).reverse();
            const displayedOfferIds = new Set(offers.map((offer) => offer.id));
            const favoriteOffers = (group.favoriteOffers ?? []).filter(
              (offer) => !displayedOfferIds.has(offer.id),
            );

            return (
              <article
                key={group.selectionKey}
                className="admin-watchdog-snap-item min-h-[100svh] min-w-0 lg:min-h-0"
              >
                <div className="admin-watchdog-carousel-frame">
                  <ListingCarousel
                    offers={offers}
                    navigationId={`${group.resultType}-watchdog-${group.watchdogId}`}
                    previousLabel={`${copy.previous}: ${copy.watchdog} ${group.watchdogId}`}
                    nextLabel={`${copy.next}: ${copy.watchdog} ${group.watchdogId}`}
                    showNavigation={false}
                    autoPlay={false}
                    loop={false}
                    supplementaryOffers={favoriteOffers}
                    leadingSlide={
                      offers.length > 0 ? (
                        <RankedEstateIntroSlide
                          offer={offers[offers.length - 1]}
                          estateType={group.estateType}
                          offerType={group.resultType}
                          questionIndex={questionStartIndex + groupIndex}
                        />
                      ) : undefined
                    }
                    trailingSlide={
                      offers.length > 0 ? (
                        <RankedEstateMembershipSlide
                          offer={offers[offers.length - 1]}
                        />
                      ) : undefined
                    }
                    renderOffer={(offer, index) => (
                      <RankedEstateSlide
                        offer={offer}
                        rank={offers.length - index}
                        city={getCity(offer, copy.noLocation)}
                      />
                    )}
                    renderSupplementaryOffer={(offer) => (
                      <RankedEstateFavoriteSlide
                        offer={offer}
                        city={getCity(offer, copy.noLocation)}
                      />
                    )}
                    renderFooter={
                      isAdmin && showHideActions
                        ? (offer) => (
                            <RankedEstateHideAction
                              onHide={() =>
                                hideOffer(group.selectionKey, offer.id)
                              }
                            />
                          )
                        : undefined
                    }
                    renderSupplementaryFooter={
                      isAdmin && showHideActions
                        ? (offer) => (
                            <RankedEstateFavoriteActions
                              onUnfavorite={() =>
                                unfavoriteOffer(group.selectionKey, offer.id)
                              }
                              onHide={() =>
                                hideOffer(group.selectionKey, offer.id)
                              }
                            />
                          )
                        : undefined
                    }
                    emptyContent={
                      <div className="flex min-h-52 items-center justify-center px-6 py-12">
                        <p className="text-center text-lg text-softiq-copy-muted dark:text-softiq-copy-inverse-muted">
                          {group.resultType === "rent"
                            ? copy.rentEmpty
                            : copy.empty}
                        </p>
                      </div>
                    }
                  />
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
